import { BarChart } from '@mui/x-charts/BarChart';

export interface DashboardBarSeries {
  label: string;
  data: number[];
  color?: string;
}

interface DashboardBarChartProps {
  categories: string[];
  series: DashboardBarSeries[];
  height?: number;
  layout?: 'vertical' | 'horizontal';
  valueFormatter?: (value: number | null) => string;
  /** Default (unset) keeps the library's default top placement. 'bottom' moves the legend below
   * the plot and reserves margin for it — used by the TV display, where charts sit in narrower
   * columns and a top/inline legend can overlap the plot area. */
  legendPosition?: 'bottom';
  /** Shows each bar's value at its edge (top for vertical, right for horizontal) — opt-in since
   * it's not wanted on every chart (e.g. dense multi-series ones get cluttered). */
  showValueLabels?: boolean;
  /** Colors each bar individually from this palette instead of every bar sharing the single
   * series' own color — only applies when there's exactly one series (a "Top N" ranking chart,
   * not a grouped/stacked comparison), since per-bar coloring and multi-series comparison are
   * mutually exclusive framings. Internally this re-models the one series as N single-point
   * series (the standard @mui/x-charts technique for per-bar color, since a bar series' `color`
   * is one value shared by every bar in it) — the legend is suppressed either way since the
   * x-axis category labels already identify each bar. */
  perItemColors?: string[];
}

/** Thin, consistently-configured wrapper around @mui/x-charts BarChart — one or more series
 * against the same category axis renders as a grouped bar chart automatically. */
export default function DashboardBarChart({
  categories,
  series,
  height = 300,
  layout = 'vertical',
  valueFormatter,
  legendPosition,
  showValueLabels,
  perItemColors,
}: DashboardBarChartProps) {
  const axis = [{ scaleType: 'band' as const, data: categories, tickLabelStyle: { fontSize: 11 } }];
  const isPerItemColored = Boolean(perItemColors?.length) && series.length === 1;
  const showsLegend = !isPerItemColored && series.length >= 2;
  const bottomMargin = legendPosition === 'bottom' && showsLegend ? 55 : 30;

  const chartSeries = isPerItemColored
    ? categories.map((category, i) => ({
        label: category,
        data: categories.map((_, j) => (j === i ? series[0].data[j] : null)),
        color: perItemColors![i % perItemColors!.length],
        valueFormatter,
      }))
    : series.map((s) => ({
        label: s.label,
        data: s.data,
        color: s.color,
        valueFormatter,
      }));

  return (
    <BarChart
      height={height}
      layout={layout}
      xAxis={layout === 'vertical' ? axis : undefined}
      yAxis={layout === 'horizontal' ? axis : undefined}
      series={chartSeries}
      barLabel={showValueLabels ? 'value' : undefined}
      margin={{ top: 10, right: layout === 'horizontal' ? 40 : 10, bottom: bottomMargin, left: layout === 'horizontal' ? 110 : 40 }}
      slotProps={{
        legend: {
          hidden: !showsLegend,
          direction: 'row',
          // Omit `position` entirely rather than setting it to `undefined` when not overriding —
          // an explicit `undefined` key still shadows @mui/x-charts' own internal default during
          // its slotProps merge, crashing LegendPerItem (it reads `position.horizontal`
          // unconditionally, with no null-guard for a missing position).
          ...(legendPosition === 'bottom' ? { position: { vertical: 'bottom' as const, horizontal: 'middle' as const } } : {}),
        },
      }}
      grid={{ horizontal: layout === 'vertical', vertical: layout === 'horizontal' }}
    />
  );
}
